import {
  useEffect, useMemo, useRef, useState, useSyncExternalStore,
} from 'react';
import {
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Title, useGetIdentity, useNotify, useTranslate,
} from 'react-admin';
import type { SubscriptionCallback } from '@react-admin/ra-realtime';
import {
  useNavigate,
} from 'react-router-dom';
import authProvider, {
  canReviewOwnAccessRequestForCustomer,
  getGlobalRolesFromStorage,
  refreshContext,
  userHasRelationToCustomerId,
} from '../../utils/authProvider';
import { typedHttpClient } from '../../utils/httpClient';
import { WebSocketService } from '../../dataProviders/xgacWebSocket';
import {
  CURRENT_CUSTOMER_CHANGED_EVENT,
  getAccessRequestCustomerSnapshot,
} from '../../lib/currentCustomer';

const subscribeCurrentCustomer = (onStoreChange: () => void) => {

  window.addEventListener(CURRENT_CUSTOMER_CHANGED_EVENT, onStoreChange);
  window.addEventListener('contextRefreshed', onStoreChange);
  return () => {

    window.removeEventListener(CURRENT_CUSTOMER_CHANGED_EVENT, onStoreChange);
    window.removeEventListener('contextRefreshed', onStoreChange);

  };

};

type DurationKey = '1d' | '2d' | '1w' | '2w';

const DURATION_KEYS: DurationKey[] = ['1d', '2d', '1w', '2w'];

const DURATION_I18N: Record<DurationKey, string> = {
  '1d': 'resources.customer_access_request.options.duration_1_day',
  '2d': 'resources.customer_access_request.options.duration_2_days',
  '1w': 'resources.customer_access_request.options.duration_1_week',
  '2w': 'resources.customer_access_request.options.duration_2_weeks',
};

/** Approximate ms between createdAt and validUntil for each preset (best-effort display when prefilling). */
const DURATION_MS: Record<DurationKey, number> = {
  '1d': 1 * 86400000,
  '2d': 2 * 86400000,
  '1w': 7 * 86400000,
  '2w': 14 * 86400000,
};

/** Best-effort: map stored window (createdAt → validUntil) to nearest duration preset for read-only display. */
const durationKeyFromRequestWindow = (createdAt: string, validUntil: string): DurationKey => {

  const ms = new Date(validUntil).getTime() - new Date(createdAt).getTime();
  let best: DurationKey = '1d';
  let bestDiff = Infinity;
  for (const k of DURATION_KEYS) {

    const diff = Math.abs(ms - DURATION_MS[k]);
    if (diff < bestDiff) {

      bestDiff = diff;
      best = k;

    }

  }
  return best;

};

const addDuration = (from: Date, key: DurationKey): string => {

  const d = new Date(from.getTime());
  switch (key) {

  case '1d':
    d.setDate(d.getDate() + 1);
    break;
  case '2d':
    d.setDate(d.getDate() + 2);
    break;
  case '1w':
    d.setDate(d.getDate() + 7);
    break;
  case '2w':
    d.setDate(d.getDate() + 14);
    break;
  default:
    break;

  }
  return d.toISOString();

};

const requestedRolesFromGlobal = (globalRoles: string[]): string[] => {

  if (globalRoles.includes('support_global')) {

    return ['support_admin'];

  }
  if (globalRoles.includes('sales_global')) {

    return ['sales_admin'];

  }
  return [];

};

type PaginateQuery = Parameters<
  ReturnType<typeof typedHttpClient>['customerUserRelations']['customerUserRelationsControllerPaginate']
>[0];

type PaginateRow = NonNullable<
  Awaited<ReturnType<ReturnType<typeof typedHttpClient>['customerUserRelations']['customerUserRelationsControllerPaginate']>>['data']
>['result'][number];

const approveReviewPayload = (identityUserId: string | number | undefined) => ({
  type: 'approved' as const,
  reviewedBy: {
    _id: identityUserId != null ? String(identityUserId) : '',
    _ref: 'User' as const,
  },
  reviewedAt: new Date().toISOString(),
});

export const CustomerAccessRequestPage = () => {

  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const { data: identity, isLoading: identityLoading } = useGetIdentity();

  const [duration, setDuration] = useState<DurationKey>('1d');
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingRelationId, setPendingRelationId] = useState<string | null>(null);
  const [pendingRelationCustomerId, setPendingRelationCustomerId] = useState<string | null>(null);
  /** Existing open access request for this user+customer (prefilled, fields locked until reviewed or force-approved). */
  const [hasOpenAccessRequest, setHasOpenAccessRequest] = useState(false);
  /** Server validUntil for PATCH when locked (exact ISO from relation). */
  const [openRequestValidUntil, setOpenRequestValidUntil] = useState<string | null>(null);
  const outcomeHandledRef = useRef(false);
  const pendingFromSubmitRef = useRef(false);
  const prevCustomerIdRef = useRef('');

  const customerSnapshot = useSyncExternalStore(subscribeCurrentCustomer, getAccessRequestCustomerSnapshot, () => '');
  const customer = useMemo(() => {

    if (!customerSnapshot) {

      return null;

    }
    try {

      return JSON.parse(customerSnapshot) as { value: string; label: string };

    } catch {

      return null;

    }

  }, [customerSnapshot]);
  const customerId = customer?.value ?? '';

  const globalRoles = useMemo(() => getGlobalRolesFromStorage(), []);
  const canUsePage = authProvider.isAdmin()
    || globalRoles.includes('sales_global')
    || globalRoles.includes('support_global');

  useEffect(() => {

    if (!canUsePage) {

      notify('resources.config.text.no_access', { type: 'warning' });
      navigate('/');

    }

  }, [canUsePage, navigate, notify]);

  const fieldsLocked = hasOpenAccessRequest;
  const forceDisabledByDuration = duration === '1w' || duration === '2w';
  /** When form is locked (existing open request), force stays available even for long durations. */
  const forceSwitchDisabled = fieldsLocked ? false : forceDisabledByDuration;

  const showForceSwitch = useMemo(
    () => canReviewOwnAccessRequestForCustomer(),
    [],
  );

  useEffect(() => {

    if (fieldsLocked) {

      return;

    }
    if (forceDisabledByDuration && force) {

      setForce(false);

    }

  }, [fieldsLocked, forceDisabledByDuration, force]);

  useEffect(() => {

    if (!showForceSwitch && force) {

      setForce(false);

    }

  }, [showForceSwitch, force]);

  useEffect(() => {

    if (prevCustomerIdRef.current !== customerId) {

      prevCustomerIdRef.current = customerId;
      outcomeHandledRef.current = false;
      setHasOpenAccessRequest(false);
      setOpenRequestValidUntil(null);
      setReason('');
      setDuration('1d');
      setForce(false);
      setSubmitting(false);
      if (!pendingRelationId) {

        pendingFromSubmitRef.current = false;
        setPendingRelationCustomerId(null);

      }

    }

    if (!canUsePage || identityLoading || !customerId || !identity?.id) {

      if (!identityLoading && !identity?.id) {

        if (!pendingFromSubmitRef.current) {

          setPendingRelationId(null);
          setPendingRelationCustomerId(null);

        }
        setHasOpenAccessRequest(false);
        setOpenRequestValidUntil(null);

      }
      return undefined;

    }
    let cancelled = false;

    const run = async () => {

      try {

        const query: PaginateQuery = {
          customer: customerId,
          $offset: 0,
          $max: 25,
          'user._id': identity.id,
          'customer._id': customerId,
          'accessRequest.review': 'null',
          'accessRequest.validUntil': '!null',
        } as PaginateQuery;
        const res = await typedHttpClient().customerUserRelations.customerUserRelationsControllerPaginate(query);
        if (cancelled) {

          return;

        }
        const rows = res.data?.result ?? [];
        const now = new Date();
        const open = rows.find((row: PaginateRow) => {

          const vu = row.accessRequest?.validUntil;
          const rev = row.accessRequest?.review;
          return vu != null && rev == null && new Date(vu) > now;

        });
        if (open?._id && open.accessRequest?.validUntil && open.accessRequest?.reason != null) {

          if (!pendingFromSubmitRef.current) {

            setPendingRelationId((prev) => {

              if (prev !== open._id) {

                outcomeHandledRef.current = false;

              }
              return open._id;

            });
            setPendingRelationCustomerId(customerId);
            setHasOpenAccessRequest(true);
            setOpenRequestValidUntil(open.accessRequest.validUntil);
            setReason(open.accessRequest.reason);
            setDuration(durationKeyFromRequestWindow(open.createdAt, open.accessRequest.validUntil));

          } else if (pendingRelationCustomerId === customerId) {

            setPendingRelationId(open._id);
            setHasOpenAccessRequest(true);
            setOpenRequestValidUntil(open.accessRequest.validUntil);

          }

        } else {

          setHasOpenAccessRequest(false);
          setOpenRequestValidUntil(null);
          if (!pendingFromSubmitRef.current) {

            setPendingRelationId(null);
            setPendingRelationCustomerId(null);

          }

        }

      } catch {

        if (!cancelled) {

          setHasOpenAccessRequest(false);
          setOpenRequestValidUntil(null);
          if (!pendingFromSubmitRef.current) {

            setPendingRelationId(null);
            setPendingRelationCustomerId(null);

          }

        }

      }

    };

    run();

    return () => {

      cancelled = true;

    };

  }, [
    canUsePage,
    customerId,
    identity?.id,
    identityLoading,
    pendingRelationCustomerId,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    if (!pendingRelationId || !pendingRelationCustomerId) {

      return undefined;

    }
    let cancelled = false;
    const socketService = new WebSocketService();
    const relationId = pendingRelationId;
    const customerForQuery = pendingRelationCustomerId;

    socketService.setActiveIdsPerResource('customer-user-relations', [relationId]);

    const handler: SubscriptionCallback = (event) => {

      if (cancelled || outcomeHandledRef.current) {

        return;

      }
      const id = event.payload?.id as string | undefined;
      if (!id || id !== relationId) {

        return;

      }
      const runCheck = async () => {

        if (cancelled || outcomeHandledRef.current) {

          return;

        }

        try {

          const res = await typedHttpClient().customerUserRelations.customerUserRelationsControllerFindById(
            relationId,
            { customer: customerForQuery },
          );
          const review = res.data?.accessRequest?.review;
          if (review == null) {

            return;

          }
          if (review.type === 'approved') {

            outcomeHandledRef.current = true;
            if (cancelled) {

              return;

            }
            pendingFromSubmitRef.current = false;
            setHasOpenAccessRequest(false);
            setOpenRequestValidUntil(null);
            setPendingRelationId(null);
            setPendingRelationCustomerId(null);
            notify('resources.customer_access_request.notifications.request_granted', { type: 'success' });
            await refreshContext();
            navigate('/');
            return;

          }
          if (review.type === 'rejected') {

            outcomeHandledRef.current = true;
            if (cancelled) {

              return;

            }
            pendingFromSubmitRef.current = false;
            setHasOpenAccessRequest(false);
            setOpenRequestValidUntil(null);
            setPendingRelationId(null);
            setPendingRelationCustomerId(null);
            notify('resources.customer_access_request.notifications.request_denied', { type: 'warning' });

          }

        } catch {

          // ignore; a later websocket event may succeed

        }

      };

      runCheck();

    };

    socketService.subscribe('resource/customer-user-relations', handler);
    return () => {

      cancelled = true;
      socketService.unsubscribe('resource/customer-user-relations');

    };

  }, [pendingRelationId, pendingRelationCustomerId, notify, navigate]);

  const onSubmit = async () => {

    if (fieldsLocked && !force) {

      return;

    }
    if (!customerId) {

      notify('resources.customer_access_request.validation.customer_required', { type: 'warning' });
      return;

    }
    const trimmed = reason.trim();
    if (trimmed.length < 10) {

      notify('resources.customer_access_request.validation.reason_min_length', { type: 'warning' });
      return;

    }
    if (force && !fieldsLocked && (duration !== '1d' && duration !== '2d')) {

      notify('resources.customer_access_request.validation.force_short_duration_only', { type: 'warning' });
      return;

    }
    const roles = requestedRolesFromGlobal(globalRoles);
    if (!roles.length) {

      notify('resources.customer_access_request.validation.no_global_role', { type: 'warning' });
      return;

    }

    if (fieldsLocked && openRequestValidUntil && pendingRelationId && force) {

      const submitCustomerId = customerId;
      setSubmitting(true);
      try {

        await typedHttpClient().customerUserRelations.customerUserRelationsControllerReviewAccessRequest(
          String(pendingRelationId),
          { review: approveReviewPayload(identity?.id) as never },
        );
        if (submitCustomerId !== prevCustomerIdRef.current) {

          return;

        }
        notify('resources.customer_access_request.notifications.request_granted', { type: 'success' });
        await refreshContext();
        navigate('/');

      } catch (e: unknown) {

        const msg = e && typeof e === 'object' && 'message' in e ? String((e as Error).message) : 'ra.notification.http_error';
        notify(msg, { type: 'warning' });

      } finally {

        if (submitCustomerId === prevCustomerIdRef.current) {

          setSubmitting(false);

        }

      }
      return;

    }

    const validUntil = addDuration(new Date(), duration);
    const submitCustomerId = customerId;
    setSubmitting(true);
    try {

      const body: Record<string, unknown> = {
        customer: { _id: customerId, _ref: 'Customer' },
        roles,
        accessRequest: {
          reason: trimmed,
          validUntil,
        },
      };
      const createRes = await typedHttpClient().customerUserRelations.customerUserRelationsControllerCreateAccessRequest(
        body as never,
      );
      if (submitCustomerId !== prevCustomerIdRef.current) {

        return;

      }
      if (force) {

        await typedHttpClient().customerUserRelations.customerUserRelationsControllerReviewAccessRequest(
          String(createRes.data?._id ?? ''),
          { review: approveReviewPayload(identity?.id) as never },
          { customer: customerId },
        );
        notify('resources.customer_access_request.notifications.request_granted', { type: 'success' });
        await refreshContext();
        navigate('/');

      }
      const createdId = createRes.data?._id;
      if (createdId && !force) {

        outcomeHandledRef.current = false;
        pendingFromSubmitRef.current = true;
        setHasOpenAccessRequest(true);
        setOpenRequestValidUntil(validUntil);
        setPendingRelationCustomerId(customerId);
        setPendingRelationId(createdId);

      }
      notify('resources.customer_access_request.notifications.submit_success', { type: 'success' });

    } catch (e: unknown) {

      const msg = e && typeof e === 'object' && 'message' in e ? String((e as Error).message) : 'ra.notification.http_error';
      notify(msg, { type: 'warning' });

    } finally {

      if (submitCustomerId === prevCustomerIdRef.current) {

        setSubmitting(false);

      }

    }

  };

  if (!canUsePage) {

    return null;

  }

  const showGlobalHint = Boolean(customerId)
    && (globalRoles.includes('sales_global') || globalRoles.includes('support_global'))
    && !userHasRelationToCustomerId(customerId);

  const submitDisabled = submitting || (fieldsLocked && !force);

  return (
    <>
      <Title title={translate('resources.customer_access_request.title')} />
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {translate('resources.customer_access_request.title')}
          </Typography>
          {showGlobalHint && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {translate('resources.customer_access_request.text.redirected_from_customer_switch')}
            </Typography>
          )}
          {customer ? (
            <Typography variant="body1" sx={{ mb: 2 }}>
              {translate('resources.customer_access_request.text.requesting_for_customer', { customer: customer.label })}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {translate('resources.customer_access_request.validation.customer_required')}
            </Typography>
          )}
          {fieldsLocked && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {translate(showForceSwitch
                ? 'resources.customer_access_request.text.open_request_locked_with_force'
                : 'resources.customer_access_request.text.already_pending')}
            </Typography>
          )}
          <FormControl fullWidth margin="normal" disabled={fieldsLocked}>
            <InputLabel id="car-duration-label">{translate('resources.customer_access_request.fields.duration')}</InputLabel>
            <Select
              labelId="car-duration-label"
              label={translate('resources.customer_access_request.fields.duration')}
              value={duration}
              onChange={(e) => setDuration(e.target.value as DurationKey)}
            >
              {DURATION_KEYS.map((k) => (
                <MenuItem key={k} value={k}>{translate(DURATION_I18N[k])}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            multiline
            minRows={3}
            label={translate('resources.customer_access_request.fields.reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            disabled={fieldsLocked}
          />
          {showForceSwitch && (
            <FormControlLabel
              control={(
                <Switch
                  checked={force}
                  disabled={forceSwitchDisabled}
                  onChange={(e) => setForce(e.target.checked)}
                />
              )}
              label={translate('resources.customer_access_request.fields.force')}
            />
          )}
          <div style={{
            marginTop: '10px',
            display: 'block',
          }}>
            <Button variant="contained" color="primary" onClick={onSubmit} disabled={submitDisabled}>
              {translate('resources.customer_access_request.actions.submit')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );

};
